/**
 * FHIR Task mapping helpers.
 *
 * Translates between the UI model (form values, table row model) and the
 * canonical FHIR Task resource.  All Medplum-specific logic is concentrated
 * here so the page / components stay presentation-only.
 */

import type { Coding, Reference, Task } from '@medplum/fhirtypes';
import {
  TASK_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import { getDueDate } from '@unify/fhir-model/resources/task';
import type { TaskFormValues, TaskInboxStatus, TaskPriority } from '../types';
import { toInboxStatus } from '../types';

// Historical ad-hoc coding may still appear on Tasks created before custom
// create was removed. Inbox create no longer writes this code.

export const AD_HOC_TASK_CODE = 'ad-hoc' as const;
export const AD_HOC_TASK_DISPLAY = 'Ad-hoc Task' as const;

// ─── Due date ──────────────────────────────────────────────────────────
// The project convention is restriction.period.end for the task due date.
// This is consistent with the fhir-model getter/setter and the mock data.

export function getTaskDueDate(task: Task): string | undefined {
  return getDueDate(task);
}

export function setTaskDueDate(task: Task, dueDate: string | null): Task {
  if (!dueDate) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { restriction: _removed, ...rest } = task;
    return rest as Task;
  }
  return {
    ...task,
    restriction: {
      ...task.restriction,
      period: {
        ...task.restriction?.period,
        end: dueDate,
      },
    },
  };
}

export function isTaskOverdue(task: Task): boolean {
  const dueDate = getTaskDueDate(task);
  if (!dueDate) return false;
  if (task.status === 'completed' || task.status === 'cancelled') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  return due < today;
}

// ─── Status mapping ────────────────────────────────────────────────────
// FHIR Task.status and our UI TaskStatus are identical strings, so the
// mapping is trivial.  We keep named functions for explicitness.

export function uiStatusToFhirStatus(status: TaskInboxStatus): Task['status'] {
  return status;
}

export function fhirStatusToUiStatus(status: Task['status']): TaskInboxStatus {
  return toInboxStatus(status);
}

// ─── Priority mapping ──────────────────────────────────────────────────

export function uiPriorityToFhirPriority(priority: TaskPriority): Task['priority'] {
  return priority;
}

export function fhirPriorityToUiPriority(priority: Task['priority']): TaskPriority {
  return (priority as TaskPriority) || 'routine';
}

// ─── Task ↔ form values ────────────────────────────────────────────────

export function taskToFormValues(task: Task): TaskFormValues {
  return {
    description: task.description || '',
    status: fhirStatusToUiStatus(task.status),
    priority: fhirPriorityToUiPriority(task.priority),
    dueDate: getTaskDueDate(task) || null,
    assignee: task.owner ? { reference: task.owner.reference, display: task.owner.display } : null,
    patient: task.for ? { reference: task.for.reference, display: task.for.display } : null,
    notes: task.note?.[0]?.text || '',
  };
}

export function formValuesToAdHocTaskResource(
  values: TaskFormValues,
  context: { requesterRef?: Reference },
): Task {
  const task: Task = {
    resourceType: 'Task',
    status: uiStatusToFhirStatus(values.status),
    intent: 'order',
    priority: uiPriorityToFhirPriority(values.priority),
    description: values.description.trim(),
    authoredOn: new Date().toISOString(),
    code: {
      coding: [{ system: TASK_TYPE_SYSTEM, code: AD_HOC_TASK_CODE, display: AD_HOC_TASK_DISPLAY }],
    },
  };

  if (values.dueDate) task.restriction = { period: { end: values.dueDate } };
  if (values.assignee) {
    task.owner = { reference: values.assignee.reference, display: values.assignee.display };
  }
  if (values.patient) {
    task.for = { reference: values.patient.reference, display: values.patient.display };
  }
  if (context.requesterRef) {
    task.requester = {
      reference: context.requesterRef.reference,
      display: context.requesterRef.display,
    };
  }
  if (values.notes.trim()) task.note = [{ text: values.notes.trim() }];
  return task;
}

/** Apply form-value changes to an existing Task for update. */
export function mergeTaskUpdates(existing: Task, values: TaskFormValues): Task {
  let updated: Task = {
    ...existing,
    status: uiStatusToFhirStatus(values.status),
    priority: uiPriorityToFhirPriority(values.priority),
    description: values.description.trim(),
    lastModified: new Date().toISOString(),
  };

  updated = setTaskDueDate(updated, values.dueDate);

  updated.owner = values.assignee ? { reference: values.assignee.reference, display: values.assignee.display } : undefined;
  updated.for = values.patient ? { reference: values.patient.reference, display: values.patient.display } : undefined;
  updated.note = values.notes.trim() ? [{ text: values.notes.trim() }] : undefined;

  return updated;
}

// ─── Task type label ───────────────────────────────────────────────────

/**
 * The Task type coding: the TASK_TYPE_SYSTEM coding (system-managed Tasks put the category
 * coding first), falling back to the first coding for legacy Tasks without a canonical type.
 */
export function getTaskTypeCoding(task: Task): Coding | undefined {
  const codings = task.code?.coding;
  return codings?.find((c) => c.system === TASK_TYPE_SYSTEM) ?? codings?.[0];
}

export function getTaskTypeLabel(task: Task): string {
  const coding = getTaskTypeCoding(task);
  return coding?.display || coding?.code || 'Other';
}

export function getTaskTypeCode(task: Task): string {
  return getTaskTypeCoding(task)?.code ?? 'other';
}

/** True when the task was created manually (ad-hoc) rather than by a system rule. */
export function isAdHocTask(task: Task): boolean {
  return task.code?.coding?.some(
    (c) => c.system === TASK_TYPE_SYSTEM && c.code === AD_HOC_TASK_CODE,
  ) ?? false;
}
